import { getProgressBar } from '../../progress-bar/executeSteps'
import { truncateCollectionName } from '../truncateCollectionName'
import {
  createAndPopulatePresetCollection,
  PAGE_TYPES,
  runTransactionWithRetry,
  UNDO_REDO_LABELS,
} from '@wix/wix-data-client-common'
import { getPresetDataForNewCollection } from '../utils/getPresetDataForNewCollection'
import { getRouterPrefixAndPattern } from '../add-dynamic-item-page/getRouterPrefixAndPattern'
import { addPage } from './addPage'
import { addRouter } from './addRouter'

import type {
  EditorSDK,
  FedopsLogger,
  I18n,
  Logger,
} from '@wix/wix-data-client-common-standalone'
import type { PageRef } from '@wix/editor-platform-sdk-types'
import type { PresetDefinition } from '../../presets/types'
import type {
  AppApi,
  Editor3DynamicPagesBiParams,
} from '@wix/wix-data-client-common'
import type { CollectionsApi } from '@wix/wix-code-collections-api'
import type { WixDataApi } from '@wix/wix-data-core'
import { generateUniquePageLinkId } from '../../slug/generatePageLinkId'
import { dynamicPageCustomRouterCreatePageClicked } from '@wix/bi-logger-wix-code-data/v2'
import type { PageAddResult } from '../add-cms-pages/addCmsPages'

type Params = {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  wixDataApi: WixDataApi
  i18n: I18n
  fedopsLogger: FedopsLogger
  logger: Logger
  biParams: Editor3DynamicPagesBiParams
}

export const addItemPage = async ({
  editorSdkProxy,
  appApi,
  preset,
  collectionsApi,
  collectionId,
  pageLinkId,
}: {
  editorSdkProxy: EditorSDK
  appApi: AppApi
  preset: PresetDefinition
  collectionsApi: CollectionsApi
  collectionId: string
  pageLinkId: string
}): Promise<PageAddResult> => {
  const newCollectionSchema = await collectionsApi.get(collectionId)

  const { newRouterPrefix, pattern } = await getRouterPrefixAndPattern({
    editorSdkProxy,
    appApi,
    newCollectionSchema,
  })

  const { pageRef } = await addPage({
    editorSdkProxy,
    preset,
    newCollectionName: newCollectionSchema.displayName,
  })

  await addRouter({
    editorSdkProxy,
    newCollectionSchema,
    presetRouter: preset.routers[0],
    pageRef,
    pattern,
    newRouterPrefix,
    pageLinkId,
  })

  return { pageRef, newRouterPrefix, pattern, schema: newCollectionSchema }
}

const interactionName = 'addCollectionAndDynamicItemPage'

export const addCollectionAndDynamicItemPage = async ({
  editorSdkProxy,
  appApi,
  wixDataApi,
  preset,
  collectionsApi,
  i18n,
  fedopsLogger,
  logger,
  biParams,
}: Params) => {
  fedopsLogger.interactionStarted(interactionName)

  const progressBar = getProgressBar({
    editorSdkProxy,
    logger,
    totalSteps: 2,
    title: i18n.t('Editor3_Add_Pages_Progress_Bar_Title_Creating_Item_Pages'),
  })
  await progressBar.open()

  let pageRef: PageRef

  try {
    const { collectionId, collectionName, schema, records } =
      await getPresetDataForNewCollection({
        preset,
        collectionsApi,
      })

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Setting_Up_Collection',
        {
          collection_name: truncateCollectionName(collectionName),
        },
      ),
    })

    await createAndPopulatePresetCollection({
      collectionsApi,
      collectionId,
      collectionName,
      wixDataApi,
      schema,
      records,
    })

    appApi.refreshCollectionItemCounts([collectionId])

    await progressBar.update({
      stepTitle: i18n.t(
        'Editor3_Add_Pages_Progress_Bar_Step_Title_Adding_Items_Page',
      ),
    })

    const pageLinkId = await generateUniquePageLinkId({ editorSdkProxy })

    const result = await runTransactionWithRetry(editorSdkProxy, async () => {
      const result = await addItemPage({
        editorSdkProxy,
        appApi,
        preset,
        collectionsApi,
        collectionId,
        pageLinkId,
      })

      return result
    })

    pageRef = result.pageRef
    const { newRouterPrefix, pattern, schema: newCollectionSchema } = result

    logger.bi(
      dynamicPageCustomRouterCreatePageClicked({
        page_type: PAGE_TYPES.ITEM,
        collection_name: newCollectionSchema.displayName,
        collection_id: collectionId,
        new_url: `${newRouterPrefix}${pattern}`,
        is_auto_bound: false,
        new_collection: true,
        pageId: pageRef.id,
        action_type: 'add_dynamic_page',
        origin: biParams?.origin,
        template_id: preset.id,
      }),
    )

    await progressBar.update()
  } finally {
    await progressBar.close()
  }

  await appApi.handleChangedRouters({ pageRefs: [pageRef] })

  await editorSdkProxy.history.add({
    label: UNDO_REDO_LABELS.UPDATE_ROUTERS,
    params: { pageRefs: [pageRef] },
  })

  fedopsLogger.interactionEnded(interactionName)

  await editorSdkProxy.pages.navigateTo({ pageRef })

  return { pageRef }
}
